<script lang="ts" setup>
import { FetchPaymentMethodsPayload, HostedFieldsTokenizePayload } from 'braintree-web';
import { computed, ref } from 'vue';

import CreditCardLogo from '@/components/base/assets/CreditCardLogo.vue';
import RadioGroup from '@/components/base/form/RadioGroup.vue';
import RadioGroupItem from '@/components/base/form/RadioGroupItem.vue';
import PopperWrapper from '@/components/base/layout/PopperWrapper.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import CreditCardForm from '@/components/checkout/steps/payment/CreditCardForm.vue';
import { CheckoutEvents } from '@/utils/analytics';

interface Props {
  braintreeToken: string;
  containsAutoDelivery?: boolean;
  modelValue?: FetchPaymentMethodsPayload | HostedFieldsTokenizePayload;
  saveCardOption?: boolean;
  savedCards: FetchPaymentMethodsPayload[];
}

const props = withDefaults(defineProps<Props>(), { saveCardOption: true });
const emit = defineEmits<{
  cancel: [];
  saved: [];
  'update:modelValue': [HostedFieldsTokenizePayload | FetchPaymentMethodsPayload | undefined];
}>();

const cardInUse = computed<HostedFieldsTokenizePayload | string | undefined>({
  get: () => props.modelValue?.nonce,
  set: (selection) => {
    if (typeof selection === 'string') {
      let value: typeof props.modelValue = props.savedCards.find((c) => c.nonce === selection);
      if (!value && selection === props.modelValue?.nonce) {
        value = props.modelValue;
      }
      emit('update:modelValue', value);
    }
    if (typeof selection === 'object') {
      emit('update:modelValue', selection);
    }
  },
});
const newCard = ref<HostedFieldsTokenizePayload>();
const selectedCard = ref<string | undefined>(cardInUse.value as unknown as string);
const transientCard = ref<HostedFieldsTokenizePayload>();
if (cardInUse.value && !props.savedCards.find((c) => c.nonce === cardInUse.value)) {
  transientCard.value = { ...props.modelValue } as HostedFieldsTokenizePayload;
}

function adaptNewCard(card?: HostedFieldsTokenizePayload) {
  if (!card) return undefined;

  const { cardType, lastFour } = card.details;
  const description = `${cardType} ending in ${lastFour}`.replace(
    'American Express ending in',
    cardType,
  );
  return {
    ...card,
    description,
  };
}

const savedCardsWithTransient = computed(() => {
  if (transientCard.value) {
    const { details, type } = transientCard.value;
    if (details && type !== 'PayPal') return [transientCard.value, ...props.savedCards];
  }
  return props.savedCards;
});

const onSelectCreditCard = () => {
  CheckoutEvents.event({ action: 'Payment', label: 'Changed Credit Card' });
};

const saveAndContinue = () => {
  cardInUse.value = selectedCard.value ?? adaptNewCard(newCard.value);
  emit('saved');
};

const showNewCardForm = computed(() => selectedCard.value === undefined);
</script>

<template>
  <div aria-label="Saved credit card options" data-test="saved-card-options" role="radiogroup">
    <RadioGroup
      v-model="selectedCard"
      class="text-sm md:text-base"
      data-test="card-option-selector"
      :disabled="(card) => card.details?.cardType === 'US Bank Account' && containsAutoDelivery"
      :items="savedCardsWithTransient"
      :keyGetter="(card) => card.nonce"
      name="saved-card-option"
      :valueGetter="(card) => card.nonce"
      @input="onSelectCreditCard"
    >
      <template #default="{ item: card }">
        <div
          class="w-full cursor-pointer"
          :class="selectedCard === card.nonce ? 'font-semibold' : 'font-normal'"
        >
          {{ card.description }}
          <CreditCardLogo class="w-8 pb-1 ml-1" :cardType="card.details?.cardType" />
        </div>
        <div
          v-if="card.details?.cardType === 'US Bank Account' && containsAutoDelivery"
          class="flex items-center justify-start px-6 mt-1 md:px-8 popover-container tiny-note"
        >
          <PopperWrapper
            arrow
            class="legacy-max-width"
            hover
            offsetDistance="10"
            placement="bottom-start"
          >
            <template v-slot:content>
              <div class="p-3 text-xs text-true-gray-500" data-test="ach-ad-popper">
                Your order contains Auto-Delivery items. Unfortunately, we do not support checking
                account payments for Auto-Delivery.
              </div>
            </template>
            <SmallBodyText class="underline" data-test="ach-auto-delivery-note">
              Why is this disabled?
            </SmallBodyText>
          </PopperWrapper>
          <span id="ach-ad-popper-locator" class="sr-only">
            Checking Accounts cannot be used for Auto Delivery items
          </span>
        </div>
      </template>
      <template #bottom-options>
        <RadioGroupItem
          v-model="selectedCard"
          data-test="new-card-option-selector"
          name="saved-card-option"
          position="last"
          :value="undefined"
        >
          <div
            class="w-full font-normal cursor-pointer"
            :class="{ 'font-semibold': selectedCard === undefined }"
          >
            Add a new credit/debit card
          </div>
          <template #bottom>
            <transition name="fade">
              <CreditCardForm
                v-show="showNewCardForm"
                v-model="newCard"
                class="mt-3 cursor-default"
                :braintreeToken="braintreeToken"
                :saveCardOption="saveCardOption"
              />
            </transition>
          </template>
        </RadioGroupItem>
      </template>
    </RadioGroup>

    <div class="flex items-center mt-5 md:mt-8">
      <ThemedButton
        class="mr-7"
        theme="gray"
        :disabled="!selectedCard && (!newCard || !newCard.nonce)"
        @click="saveAndContinue"
        data-test="save-and-continue"
      >
        Save &amp; Continue
      </ThemedButton>
      <p
        class="mb-0 text-base underline cursor-pointer"
        @click="$emit('cancel')"
        @keydown.enter.prevent="$emit('cancel')"
        @keydown.space.prevent="$emit('cancel')"
        role="button"
        tabindex="0"
        data-test="cancel-button"
      >
        Cancel
      </p>
    </div>
  </div>
</template>
